import { computed, makeObservable, observable } from 'mobx';

import { type CreateTarget } from 'actions';
import { getFolderAncestors } from 'api/folders/endpoints/getFolderAncestors';
import { type WorkspaceItem, type WorkspaceParent } from 'api/workspace/endpoints/getWorkspaceContents';
import { WorkspaceContentsModel } from './WorkspaceContentsModel';
import { WorkspaceTreeModel, type WorkspaceTreeNode } from './WorkspaceTreeModel';

/**
 * Top-level workspace state: the container tree, the merged-contents pane, and
 * the current selection that links them. Router-free — a bridge hook mirrors
 * the selection to/from the URL. Pure observer views read the getters.
 */
export class WorkspaceModel {
  readonly tree = new WorkspaceTreeModel();
  readonly contents = new WorkspaceContentsModel();
  private _selected: WorkspaceParent | null = null;
  // Bumped on every selection intent (a click or a hydrate). `revealFolder`
  // captures it and only applies its deferred select if no newer intent landed
  // while its ancestors were loading — so a slow reveal can't snap the pane back.
  private _selectionSeq = 0;

  constructor() {
    makeObservable<WorkspaceModel, '_selected'>(this, {
      _selected: observable.ref,
      createTarget: computed,
    });
  }

  /**
   * The create target derived from the selection: a selected zone targets its
   * root (folderId null); a selected folder targets itself. Null when nothing is
   * selected, or when a folder's zone isn't known yet (not loaded in the tree) —
   * the toolbar then creates without a pre-target. Computed so the reference is
   * stable while the selection/tree are unchanged — lets row-action consumers
   * memoize on it.
   */
  get createTarget(): CreateTarget | null {
    const selected = this._selected;
    if (selected === null) return null;
    const item = this.tree.findItem(selected);
    // A create always needs a zone. Every tree container (zone or folder) has
    // one, but the shared WorkspaceItem types zoneId/zoneName as nullable
    // (they're folder-derived for the zoneless leaf types). Narrow honestly —
    // no known zone means no pre-target — rather than fabricating one from the
    // item's own id/name.
    if (item === undefined || item.zoneId === null || item.zoneName === null) return null;
    return {
      zoneId: item.zoneId,
      zoneName: item.zoneName,
      folderId: item.type === 'folder' ? item.id : null,
    };
  }

  /** The selected container locator (`zone:<id>` / `folder:<id>`), or null. */
  get selected(): WorkspaceParent | null {
    return this._selected;
  }

  /**
   * The selected container's child folders (from the tree model's own fetch, so
   * folders aren't fetched twice), shown first in the contents pane above the
   * paginated objects.
   */
  get folderRows(): readonly WorkspaceItem[] {
    const selected = this._selected;
    return selected === null ? [] : this.tree.childFolders(selected);
  }

  /**
   * Reload the selected container after a create/edit/delete — both its child
   * folders (which live in the tree) and its objects (the contents pane). Passed
   * as the action `refresh` so folder mutations update the tree, not just the
   * table. Stable identity for row-action memoization.
   */
  refresh = (): void => {
    const selected = this._selected;
    if (selected === null) return;
    this.tree.reloadChildren(selected);
    this.contents.refresh();
  };

  /** Select a container (from a tree click) and load its contents. */
  select = (locator: WorkspaceParent): void => {
    if (this._selected === locator) return;
    this._selectionSeq++;
    this._selected = locator;
    // Load the container's child folders (shared with the tree) and its objects.
    void this.tree.ensureLoaded(locator);
    void this.contents.load(locator);
  };

  /**
   * Restore a selection from the URL. For a folder, first expand the tree along
   * its ancestor path (so a refreshed/bookmarked deep folder is revealed), then
   * select it; a zone is already a root, so just select.
   */
  hydrate = (locator: WorkspaceParent): void => {
    const seq = ++this._selectionSeq;
    if (locator.startsWith('folder:')) {
      void this.revealFolder(Number(locator.slice('folder:'.length)), locator, seq);
    } else {
      this.select(locator);
    }
  };

  private async revealFolder(folderId: number, locator: WorkspaceParent, seq: number): Promise<void> {
    try {
      const { zoneId, folderIds } = await getFolderAncestors(folderId);
      const path: WorkspaceParent[] = [`zone:${zoneId}`, ...folderIds.map((id) => `folder:${id}` as WorkspaceParent)];
      await this.tree.expandPath(path);
    } catch {
      // If the ancestor path can't be resolved, still select so contents load.
    }
    // A click or newer hydrate during the await supersedes this reveal — don't
    // snap the selection back to the deep-linked folder.
    if (seq !== this._selectionSeq) return;
    this.select(locator);
  }

  /** Bridge for the tree view's onSelect (ignores the loading placeholder). */
  selectNode = (node: WorkspaceTreeNode): void => {
    if (node.isLoadingPlaceholder) return;
    this.select(node.key as WorkspaceParent);
  };
}
